// Umbrales, pesos y semáforo del Listado de Clientes (UX 2.1).
//
// Viven en un archivo propio a propósito: son los números que vamos a calibrar mirando la pantalla
// (¿a partir de qué desvío una fila "requiere atención"?), y no queremos ir a buscarlos entre el
// render. Todo el cálculo es client-side sobre el payload de `agency/clients/overview/`.

// Calibración vigente: medida el 2026-07-30 sobre el portfolio REAL de las tres agencias del flag
// (87 clientes activos con líneas). Con los valores originales el semáforo no discriminaba: 75% de
// las filas quedaba en rojo y sólo 12,6% podía llegar a verde. Los cortes de abajo son el escenario
// "E2" de esa medición → rojo ~32%, oro ~18%, verde ~30% en Bakián.

// --- Semáforo del gasto -----------------------------------------------------------------------
// NO hay umbral acá a propósito: cuánto desvío es "advertencia" y cuánto es "crítico" lo configura
// CADA agencia (y puede afinarlo por línea) en la configuración de alertas. El backend manda en
// `budget_alert_level` el resultado de esa evaluación —la misma que dispara las alertas que la
// agencia recibe— y la pantalla sólo lo traduce a color. Antes esta pantalla recalculaba el desvío
// contra un umbral propio y terminaba contradiciendo a las alertas: filas en rojo de clientes que
// nunca dispararon una, y viceversa.
export const ALERT_LEVEL_CRITIC = 'CRITIC';
export const ALERT_LEVEL_BAD = 'BAD';

// --- Semáforo de objetivos --------------------------------------------------------------------
// Proporción de objetivos en ritmo (on_track / total). El verde no exige el 100%: un objetivo
// atrasado de diez no es lo mismo que la mitad del plan caída.
export const GOALS_RATIO_GREEN_MIN = 0.9;
export const GOALS_RATIO_GOLD_MIN = 0.4;

// --- Alertas ----------------------------------------------------------------------------------
// La unidad es la ráfaga de la Bitácora (alerta × día), y son problemas DISTINTOS: la relación
// ráfagas/problema medida es 1,5 (mediana), no un mismo problema repitiéndose toda la semana. Entre
// los clientes con alertas la mediana es 5 ráfagas semanales y el p90 son 33, así que un corte bajo
// pinta de rojo a cualquiera que tenga algo. El piso del oro importa tanto como el techo del rojo:
// con ALERTS_GOLD_MIN=1, el 66% de los clientes activos quedaba fuera de verde por una sola alerta.
export const ALERTS_RED_MIN = 15;
export const ALERTS_GOLD_MIN = 5;

// --- Pesos del score de "Atención primero" ----------------------------------------------------
export const SCORE_PER_ALERT = 10;
export const SCORE_PER_MEDIA_PLAN_PENDING = 4;
export const SCORE_SEVERITY_RED = 8;
export const SCORE_SEVERITY_GOLD = 4;

// --- Severidades ------------------------------------------------------------------------------
export const SEVERITY_RED = 'red';
export const SEVERITY_GOLD = 'gold';
export const SEVERITY_GREEN = 'green';
export const SEVERITY_GRAY = 'gray';

// Orden de los tramos (y de los separadores de grupo) en la vista "Atención primero".
export const SEVERITY_GROUPS = [SEVERITY_RED, SEVERITY_GOLD, SEVERITY_GREEN, SEVERITY_GRAY];

const SEVERITY_RANK = {
    [SEVERITY_RED]: 0,
    [SEVERITY_GOLD]: 1,
    [SEVERITY_GREEN]: 2,
    [SEVERITY_GRAY]: 3,
};

export function severityRank(severity) {
    const rank = SEVERITY_RANK[severity];
    return rank === undefined ? 9 : rank;
}

// Fracción del mes ya transcurrida. Es informativa: la marca vertical de la barra de inversión y el
// tooltip («88 % de lo esperado», `spendReason`). NO decide color — eso lo hace `spendState` con la
// evaluación de la agencia.
export function monthElapsedFraction(now = new Date()) {
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return now.getDate() / daysInMonth;
}

// Estado del presupuesto según la configuración de alertas de la agencia. `budget_alert_level` en
// null = todavía no hay evaluación para ninguna línea del cliente (sync pendiente) → no opina.
export function spendState(client) {
    const level = client.budget_alert_level;
    if (!level) return SEVERITY_GRAY;
    if (level === ALERT_LEVEL_CRITIC) return SEVERITY_RED;
    if (level === ALERT_LEVEL_BAD) return SEVERITY_GOLD;
    return SEVERITY_GREEN;
}

// `goals_on_track` puede venir null: el backend no sabe cuántos van en ritmo, así que los objetivos
// no opinan sobre la severidad ni sobre el score.
export function hasGoalsPace(client) {
    return client.goals_on_track !== null && client.goals_on_track !== undefined;
}

// Cuántos objetivos del cliente YA tienen evaluación. Uno recién creado todavía no la tiene
// (`goals_detail[].on_track === null`: sin `GoalInformation` hasta la próxima sincronización), y el
// backend lo suma a `goals_total` pero no a `goals_on_track`. Sin descontarlo, guardar el PRIMER
// objetivo de un cliente lo mostraba «0 de 1 en ritmo» y, por la regla del 40 %, lo mandaba a
// «Requieren atención» (card RVO9leLh). Sin el detalle (backend viejo) se usa el total.
export function evaluatedGoalsTotal(client) {
    const total = Number(client.goals_total || 0);
    const detail = client.goals_detail;
    if (!Array.isArray(detail) || !detail.length) return total;
    const pending = detail.filter((goal) => goal.on_track === null).length;
    return Math.max(0, total - pending);
}

// Objetivos atrasados: evaluados y fuera de ritmo. Un objetivo sin evaluar no está atrasado.
export function goalsBehind(client) {
    if (!hasGoalsPace(client)) return 0;
    return Math.max(0, evaluatedGoalsTotal(client) - Number(client.goals_on_track || 0));
}

export function goalsState(onTrack, total) {
    if (onTrack === null || onTrack === undefined) return SEVERITY_GRAY;
    const goalsTotal = Number(total || 0);
    if (!goalsTotal) return SEVERITY_GRAY;
    const ratio = Number(onTrack) / goalsTotal;
    if (ratio >= GOALS_RATIO_GREEN_MIN) return SEVERITY_GREEN;
    if (ratio >= GOALS_RATIO_GOLD_MIN) return SEVERITY_GOLD;
    return SEVERITY_RED;
}

// ¿El presupuesto del cliente está SIN evaluación reciente? El listado no recibe la frescura como
// campo propio, pero la trae la proyección: el backend manda `projection.amount` en null cuando no
// hay con qué proyectar, y de sus tres causas (sin dato fresco, gasto que no se pudo expresar en una
// sola moneda, sin ventana de ritmo) la del medio se descarta con `spend_unavailable`, que el
// payload ya manda. Lo que queda es "la línea dejó de sincronizar". Sin el campo `projection`
// (backend viejo) no se afirma nada: se lo trata como fresco.
export function hasStaleBudgetData(client) {
    if (client.spend_unavailable) return false;
    const projection = client.projection;
    return !!projection && projection.amount === null;
}

// Severidad unificada de la fila: define el borde izquierdo y el tramo del orden por atención.
export function rowSeverity(client) {
    if (!Number(client.business_units_count || 0)) return SEVERITY_GRAY;

    const spend = spendState(client);
    const goals = goalsState(client.goals_on_track, evaluatedGoalsTotal(client));
    const alerts = Number(client.alerts_count || 0);

    if (alerts >= ALERTS_RED_MIN || spend === SEVERITY_RED || goals === SEVERITY_RED) return SEVERITY_RED;
    if (alerts >= ALERTS_GOLD_MIN || spend === SEVERITY_GOLD || goals === SEVERITY_GOLD) return SEVERITY_GOLD;
    // Gris = "Sin datos para evaluar" = no hay NADA contra qué medirlo: ni presupuesto ni objetivo
    // (regla de fede y Pablo, 17/09, card 1CySGg1u). Con cualquiera de los dos, el cliente va a las
    // secciones de arriba.
    //
    // Antes el gris salía de "ningún eje pudo opinar", y un cliente con presupuesto pero SIN
    // veredicto de alerta y sin objetivos caía acá aunque estuviera gastando bien — Papelera Papelón
    // en la agencia Demo. La barra de gasto no cambia: sin veredicto sigue en el gris neutro, así que
    // la fila entra a "En ritmo" sin inventar un color de presupuesto.
    //
    // Dos matices que NO cuentan como "tiene algo contra qué medirlo" (review del 23/09):
    // - un presupuesto con el dato VIEJO (la línea dejó de sincronizar): ahí verde sería afirmar
    //   que está en orden un cliente del que no sabemos nada hace días. Decisión de fede: gris. Una
    //   alerta silenciada con el dato fresco sí va arriba.
    // - objetivos con el ritmo desconocido (`goals_on_track` null, ver `hasGoalsPace`): no opinan.
    //   Tampoco los que todavía no tienen evaluación (`evaluatedGoalsTotal`).
    const hasBudget = Number(client.total_budget || 0) > 0 && !hasStaleBudgetData(client);
    const hasGoals = evaluatedGoalsTotal(client) > 0 && hasGoalsPace(client);
    if (!hasBudget && !hasGoals) return SEVERITY_GRAY;
    return SEVERITY_GREEN;
}

// --- El motivo del gasto ----------------------------------------------------------------------
// A partir de cuántas veces el presupuesto se pregunta «¿El presupuesto está al día?» (D6 de la
// tanda iVirtual, 09/10). Un exceso así casi nunca es un gasto que se fue de las manos: es un
// presupuesto viejo (el de RN Pad era de julio de 2024 y gastaba 4,8 veces). Lo leen la fila del
// Listado y la línea de la ficha (`LineVisuals`): un solo número para las dos.
export const CHECK_BUDGET_RATIO = 3;

export const SPEND_REASON_NO_SPEND = 'no_spend';
export const SPEND_REASON_OVER_BUDGET = 'over_budget';
export const SPEND_REASON_VS_EXPECTED = 'vs_expected';

/**
 * Cómo va el gasto del mes contra el presupuesto, dicho en porcentajes y nunca en puntos (D5 de la
 * tanda iVirtual). Es el MISMO dato para el motivo de la fila y para el tooltip de la barra:
 * calcularlo en dos lados los dejaba libres de contestar distinto.
 *
 * Hasta el 09/10 esto era `paceDeltaPoints`, la RESTA del consumido menos el esperado, y la fila lo
 * leía como porcentaje: RN Pad decía «Spending 448% above pace» con 477 % gastado y 29 % esperado,
 * cuando en realidad había gastado 16 veces lo esperado. Y un cliente sin gasto salía «29 % por
 * debajo del ritmo», que no dice lo único importante: no gastó nada.
 *
 * - sin presupuesto o sin un gasto único (`spend_unavailable`) → `null`;
 * - gasto 0 con el mes andando → `no_spend`;
 * - gastó más que el presupuesto → `over_budget` con el % del presupuesto, y `checkBudget` si pasó
 *   de `CHECK_BUDGET_RATIO` veces;
 * - si no → `vs_expected` con el % de lo esperado a hoy.
 *
 * `vsExpectedPct` es consumido sobre esperado, con los DOS porcentajes redondeados como los muestra
 * el tooltip («Consumido 477 %», «Esperado 29 %» → 1.645): así el número se puede rehacer con lo que
 * está en pantalla. `null` si el esperado redondea a 0.
 */
export function spendReason(client, elapsedFraction) {
    const budget = Number(client.total_budget || 0);
    if (budget <= 0 || client.spend_unavailable) return null;

    const spend = Number(client.total_spend || 0);
    const ratio = spend / budget;
    const consumedPct = Math.round(ratio * 100);
    const expectedPct = Math.round(Number(elapsedFraction || 0) * 100);
    const vsExpectedPct = expectedPct > 0 ? Math.round((consumedPct / expectedPct) * 100) : null;

    if (spend === 0) return expectedPct > 0 ? {key: SPEND_REASON_NO_SPEND, vsExpectedPct: 0} : null;
    if (ratio > 1) {
        return {
            key: SPEND_REASON_OVER_BUDGET,
            pct: consumedPct,
            vsExpectedPct,
            checkBudget: ratio >= CHECK_BUDGET_RATIO,
        };
    }
    if (vsExpectedPct === null) return null;
    return {key: SPEND_REASON_VS_EXPECTED, pct: vsExpectedPct, vsExpectedPct};
}

// Por qué una fila está en «Requieren atención», cuando el motivo es el PRESUPUESTO (card 21t02Hjz).
// Es el único eje que la fila no decía: el rojo por alertas lo dice la columna Estado y el rojo por
// objetivos, la de Objetivos. El del presupuesto sólo aparecía al pasar el mouse por la barra, al
// lado de un «✓ Sin alertas» en verde — que cuenta alertas de RENDIMIENTO, no el ritmo del gasto.
// Devuelve el motivo de `spendReason` (o `pace_generic`, sin número), o `null` si el rojo no es por
// el presupuesto.
//
// El número sólo cuando no puede contradecir la causa (review C-app-03): el rojo es el PEOR nivel
// entre las líneas del cliente, y el número es el AGREGADO contra el mes. Con una sola línea y la
// misma ventana son lo mismo. Con varias, una sobregastada y otra atrasada suman «por debajo del
// ritmo» sobre un rojo por sobregasto; con otro período, el agregado mezcla dos ventanas. Ahí, y
// cuando el número no dice nada (justo el 100 % de lo esperado, o sin dato), el motivo va sin
// número. La excepción es `no_spend`: un cero no puede contradecir ninguna causa.
//
// «¿El presupuesto está al día?» (`checkBudget`) tiene la MISMA guarda que el número, porque sale
// del mismo agregado (decisión de fede, 09/10, review C-iv-fe-senales-01): con varias líneas el
// gasto es la unión de TODAS las activas y el presupuesto, sólo el de las que lo tienen, así que una
// línea sin presupuesto o una eventual vencida que sigue gastando llevaban el cociente a 3 sin que
// ningún presupuesto estuviera viejo. Con varias líneas la pregunta queda en la ficha, que la calcula
// línea por línea (`lineNeedsBudgetCheck`).
export function attentionReason(client, severity, elapsedFraction) {
    if (severity !== SEVERITY_RED || spendState(client) !== SEVERITY_RED) return null;
    const reason = spendReason(client, elapsedFraction);
    if (reason && reason.key === SPEND_REASON_NO_SPEND) return reason;

    const generic = {key: 'pace_generic'};
    if (Number(client.business_units_count || 0) !== 1 || client.budget_covers_other_period) return generic;
    if (!reason || (reason.key === SPEND_REASON_VS_EXPECTED && reason.pct === 100)) return generic;
    return reason;
}

// --- Las cuentas de la fila -------------------------------------------------------------------
// Contrato §3 de la tanda iVirtual: el punto sobre el logo de un canal dice si alguna cuenta de ese
// canal está caída (rojo) o, si no, si alguna reporta en otra moneda que el cliente (ámbar). Rojo le
// gana a ámbar. Hasta el 09/10 el punto era siempre ámbar y chico, aunque la cuenta llevara 17 días
// caída, y el aviso de moneda no tenía ninguno.
export const ACCOUNTS_DOWN = 'down';
export const ACCOUNTS_WARN = 'warn';

export function accountIsDown(account) {
    return !!account && account.connected === false;
}

// La misma comparación que el tooltip de la cuenta («moneda distinta de la del cliente»).
export function accountCurrencyMismatch(account, clientCurrency) {
    return !!(account && account.currency && clientCurrency && account.currency !== clientCurrency);
}

/** `ACCOUNTS_DOWN` / `ACCOUNTS_WARN` / `null` para un grupo de cuentas (las de un canal, o las del «+N»). */
export function accountsSeverity(accounts, clientCurrency) {
    const list = accounts || [];
    if (list.some(accountIsDown)) return ACCOUNTS_DOWN;
    if (list.some((account) => accountCurrencyMismatch(account, clientCurrency))) return ACCOUNTS_WARN;
    return null;
}

/** Cuántas cuentas caídas tiene el CLIENTE, no el canal: es lo que dice la fila debajo de los logos. */
export function downAccountsCount(client) {
    return ((client && client.accounts) || []).filter(accountIsDown).length;
}

// El criterio de cada grupo, en palabras, para el ⓘ de su separador (card 21t02Hjz: «¿por qué me
// dice que requiere atención? ¿Cuál es el parámetro?»). Los números salen de las MISMAS constantes
// que deciden el grupo en `rowSeverity`: escritos en el texto, recalibrar un umbral dejaba la ayuda
// mintiendo.
export function groupHelp(t, severity) {
    if (severity === SEVERITY_RED) {
        return t('clients_v2.group_help.attention', {
            alerts: ALERTS_RED_MIN, pct: Math.round(GOALS_RATIO_GOLD_MIN * 100),
        });
    }
    if (severity === SEVERITY_GOLD) {
        return t('clients_v2.group_help.watch', {
            alerts: ALERTS_GOLD_MIN, pct: Math.round(GOALS_RATIO_GREEN_MIN * 100),
        });
    }
    if (severity === SEVERITY_GREEN) return t('clients_v2.group_help.on_track');
    return t('clients_v2.group_help.no_data');
}

export function attentionScore(client, severity) {
    const alerts = Number(client.alerts_count || 0);
    const pendingPlans = Number(client.media_plan_pending || 0);
    const goalsGap = goalsBehind(client);
    const severityWeight = severity === SEVERITY_RED
        ? SCORE_SEVERITY_RED
        : severity === SEVERITY_GOLD ? SCORE_SEVERITY_GOLD : 0;

    return (alerts * SCORE_PER_ALERT)
        + (pendingPlans * SCORE_PER_MEDIA_PLAN_PENDING)
        + severityWeight
        + goalsGap;
}
